import { BaseSettingsFeature } from '../../../../features/base-settings/contract'
import { BaseConnectedMouse } from '../../../../types/base'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { sendBaseCommand, sendBaseRaw } from '../../protocols/base-command/command-io'
import type { BaseDockProfile } from '../../capabilities/base-command/profile'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { formatCompactFirmwareVersion } from '../../../../utils/verify'

/**
 * 底座通用设置实现。
 *
 * 负责不属于灯效/屏幕素材本体的能力：固件版本、2.4G 频段和子鼠标 VID/PID。
 * 恢复出厂已迁至 `base-config`（`features.config.initAllConfigs`）。
 */
export class RapooBaseSettings extends BaseSettingsFeature {
  constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {
    super(device)
  }

  /** 按底座协议读取并格式化固件版本。 */
  async getFirmwareVersion(): Promise<string> {
    if (this.profile.name === 'Telink_Base') {
      // Telink 底座一次返回 4 个版本字节，1.0 展示顺序是 Vbyte4.byte3.byte2.byte1。
      const data = await sendBaseCommand(
        this.device,
        this.profile.config,
        this.commands.get_firmware,
      )
      return `V${data.getUint8(4)}.${data.getUint8(3)}.${data.getUint8(2)}.${data.getUint8(1)}`
    }

    // VT_nrf54L_Base 拆成 USB 固件、主控固件，部分带屏型号还要追加屏幕固件版本。
    const usbFirmware = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.commands.get_firmware,
      [0x00],
    )
    const chipFirmware = await sendBaseCommand(
      this.device,
      this.profile.config,
      this.commands.get_firmware,
      [0x01],
    )

    let version = `V${this.parseCompactFirmwareVersion(usbFirmware)}-V${this.parseCompactFirmwareVersion(chipFirmware)}`
    if (this.device.manifest.model !== 'ESPR7PRO') {
      // ESPR7PRO 在 1.0 中有特殊兼容逻辑：没有第三段屏幕版本。
      const screenFirmware = await sendBaseRaw(this.device, this.profile.config, [0xd0, 0x02, 0x14])
      version += `-V${screenFirmware.getUint8(5)}.${screenFirmware.getUint8(4)}`
    }
    return version
  }

  /** 读取 2.4G 频段索引。 */
  async getChannelIndex(): Promise<number> {
    // 2.4G 频段索引：读取命令 0xd2，返回值在 byte1，范围由固件定义为 0-7。
    const data = await sendBaseRaw(this.device, this.profile.config, [0xd2])
    return data.getUint8(1)
  }

  /** 设置 2.4G 频段索引。 */
  async setChannelIndex(index: number): Promise<void> {
    if (!Number.isInteger(index) || index < 0 || index > 7) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'Channel index must be between 0 and 7')
    }
    await sendBaseRaw(this.device, this.profile.config, [0xd1, index])
  }

  /** 同时读取已配对子鼠标 VID/PID。 */
  async getConnectedMouse(): Promise<BaseConnectedMouse> {
    const [vendorId, productId] = await Promise.all([
      this.getConnectedMouseVendorId(),
      this.getConnectedMouseProductId(),
    ])
    return { vendorId, productId }
  }

  /** 读取已配对子鼠标 VID。 */
  async getConnectedMouseVendorId(): Promise<number | undefined> {
    return this.readConnectedMouseId([0xa5, 0xa4, 0x02, 0x00, 0x00])
  }

  /** 读取已配对子鼠标 PID。 */
  async getConnectedMouseProductId(): Promise<number | undefined> {
    return this.readConnectedMouseId([0xa5, 0xa4, 0x02, 0x04, 0x00])
  }

  /** VT 底座固件版本是一个压缩字节，例如 105 表示 1.5。 */
  private parseCompactFirmwareVersion(data: DataView): string {
    const value = data.getUint8(1)
    return formatCompactFirmwareVersion(value)
  }

  /** 通过底座透传命令读取子鼠标 EEPROM 中的 VID/PID。 */
  private async readConnectedMouseId(payload: number[]): Promise<number | undefined> {
    try {
      // 通过底座向子鼠标透传 EEPROM 读取：VID 地址 0x0000，PID 地址 0x0004。
      const data = await sendBaseRaw(this.device, this.profile.config, payload)
      return data.getUint8(4) + data.getUint8(5) * 256
    } catch (_error) {
      return undefined
    }
  }

  /** 当前芯片底座命令表。 */
  private get commands() {
    return this.profile.commands
  }
}
